import { useNavigate, NavigateOptions, To } from 'react-router-dom';
import { useCallback } from 'react';
import { buildLocalizedPath } from '../config/languages';
import { useCurrentLocale } from './useCurrentLocale';

/**
 * Drop-in replacement for react-router's useNavigate that automatically
 * prefixes absolute paths with the current UI language.
 *
 * Usage is identical to useNavigate:
 *   const navigate = useLocalizedNavigate();
 *   navigate('/trps');              // -> /trps on SR, /en/trps on EN
 *   navigate(-1);                   // unchanged
 *   navigate('/trps', { replace: true });
 */
export const useLocalizedNavigate = () => {
    const navigate = useNavigate();
    const lang = useCurrentLocale();

    return useCallback(
        (to: To | number, options?: NavigateOptions) => {
            if (typeof to === 'number') {
                navigate(to);
                return;
            }

            if (typeof to === 'string') {
                if (to.startsWith('/')) {
                    navigate(buildLocalizedPath(to, lang), options);
                    return;
                }
                navigate(to, options);
                return;
            }

            if (to && typeof to === 'object' && 'pathname' in to && to.pathname?.startsWith('/')) {
                navigate(
                    {
                        ...to,
                        pathname: buildLocalizedPath(to.pathname, lang),
                    },
                    options,
                );
                return;
            }

            navigate(to as To, options);
        },
        [navigate, lang],
    );
};
